/**
 * Weight Calculator — transport logistics for sauna builds.
 *
 * Converts material volumes → weights using wood density constants,
 * then classifies into transport categories with advice.
 * All densities, component weights, and thresholds are configurable.
 */
import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import { getT } from "../../i18n";
import type { Market } from "../location/types";

// ─── Types ──────────────────────────────────────────────────

export interface WeightInput {
	/** Running metres of wall studs + floor/roof joists */
	lmWallStuds: number;
	/** Running metres of floor joists (45×145) */
	lmFloorJoists?: number;
	/** Running metres of roof joists (45×145–220 depending on snow zone) */
	lmRoofJoists?: number;
	/** Actual roof joist depth in mm */
	roofJoistDepthMm?: number;
	/** Backward-compatible combined joist metres, assumed 45×145 when split values are absent */
	lmFloorRoofJoists?: number;
	/** Running metres of panels + lavar + floor boards */
	lmPanel: number;
	/** Bench slats, 28×90 */
	lmLavar: number;
	/**
	 * Bench wall ledgers (bärlinor), 45×90.
	 *
	 * The bench used to be one 28×90 row, so its framing weighed nothing. Since
	 * the BOM derives from the interior layout it also buys ledgers, front
	 * boards, cross joists and legs — real timber that a layout edit can add a
	 * lot of, and transport class is a threshold decision.
	 */
	lmBenchLedger?: number;
	/** Bench front boards, end rails, joists and legs, 45×70 */
	lmBenchFrame?: number;
	lmFloorBoards: number;
	/** Running metres of treated beams (foundation) */
	lmTreatedBeams: number;
	/** Area values */
	totalWallAreaSqm: number;
	totalRoofAreaSqm: number;
	/** Foundation plinth count */
	numPlinths: number;
	/** Running metres of exterior cladding (22×120mm) */
	lmExteriorCladding: number;
	/** Running metres of furring strips (22×45mm) */
	lmFurringStrips: number;
}

export type WeightClass = "success" | "warning" | "danger";

export interface WeightResult {
	/** Net weight of the building structure (kg) */
	buildingNetKg: number;
	/** Gross weight including margin for fasteners, moisture etc. */
	buildingGrossKg: number;
	/** Total transport weight including plinths (kg) */
	transportKg: number;
	/** Weight of plinths alone (kg) */
	plinthsKg: number;
	/** Transport classification */
	weightClass: WeightClass;
	/** Transport advice (legacy resolved string — Swedish unless market passed) */
	transportAdvice: string;
	/**
	 * Translation key for transport advice. Prefer this at the UI/PDF render
	 * boundary: `calculateMaterials` runs inside the engines without a market,
	 * so `transportAdvice` above is resolved as `se`. Resolving the key with the
	 * live market keeps the intl Material tab in English.
	 */
	transportAdviceKey:
		| "domain_weight_adviceLight"
		| "domain_weight_adviceMedium"
		| "domain_weight_adviceHeavy";
}

// ─── Calculator ─────────────────────────────────────────────

export function calculateWeight(
	input: WeightInput,
	overrides: RuleOverrides = {},
	market: Market = "se",
): WeightResult {
	const t = getT(market);
	const DENSITY_WOOD = getRuleValue<number>("weight.densityWood", overrides);
	const DENSITY_TREATED = getRuleValue<number>(
		"weight.densityTreatedWood",
		overrides,
	);
	const DENSITY_SPRUCE = getRuleValue<number>(
		"exterior.densitySpruce",
		overrides,
	);
	const PIER_KG = getRuleValue<number>("weight.pierKg", overrides);
	const DOOR_KG = getRuleValue<number>("weight.doorKg", overrides);
	const HEATER_KG = getRuleValue<number>("weight.heaterKg", overrides);
	const STONES_KG = getRuleValue<number>("weight.stonesKg", overrides);
	const INSUL_KG_SQM = getRuleValue<number>("weight.insulKgPerSqm", overrides);
	const ROOF_KG_SQM = getRuleValue<number>("weight.roofKgPerSqm", overrides);
	const GROSS_MARGIN = getRuleValue<number>(
		"weight.grossMarginFactor",
		overrides,
	);
	const LIGHT_THRESHOLD = getRuleValue<number>(
		"weight.lightThresholdKg",
		overrides,
	);
	const MEDIUM_THRESHOLD = getRuleValue<number>(
		"weight.mediumThresholdKg",
		overrides,
	);
	const lmFloorJoists = input.lmFloorJoists ?? input.lmFloorRoofJoists ?? 0;
	const lmRoofJoists = input.lmRoofJoists ?? 0;
	const roofJoistDepthM = (input.roofJoistDepthMm ?? 145) / 1000;
	const combinedLegacyJoists = input.lmFloorRoofJoists ?? 0;
	const totalJoistVolume =
		input.lmFloorJoists !== undefined || input.lmRoofJoists !== undefined
			? 0.045 * 0.145 * lmFloorJoists + 0.045 * roofJoistDepthM * lmRoofJoists
			: 0.045 * 0.145 * combinedLegacyJoists;

	// Volume calculations (m³)
	const volFraming = 0.045 * 0.095 * input.lmWallStuds + totalJoistVolume;

	const volPaneling =
		0.015 * 0.09 * input.lmPanel +
		0.028 * 0.09 * input.lmLavar +
		0.028 * 0.12 * input.lmFloorBoards;

	// Bench framing is construction lumber, not panelling — same species and
	// density, but its own sections.
	const volBenchFraming =
		0.045 * 0.09 * (input.lmBenchLedger ?? 0) +
		0.045 * 0.07 * (input.lmBenchFrame ?? 0);

	const volTreated = 0.045 * 0.145 * input.lmTreatedBeams;

	// Exterior facade volume (spruce, 460 kg/m³)
	const volCladding = 0.022 * 0.12 * input.lmExteriorCladding;
	const volFurring = 0.022 * 0.045 * input.lmFurringStrips;

	// Weight calculations (kg)
	const wFraming = (volFraming + volBenchFraming) * DENSITY_WOOD;
	const wPaneling = volPaneling * DENSITY_WOOD;
	const wTreated = volTreated * DENSITY_TREATED;
	const wExterior = (volCladding + volFurring) * DENSITY_SPRUCE;
	const wInsulRoof =
		input.totalWallAreaSqm * INSUL_KG_SQM +
		input.totalRoofAreaSqm * ROOF_KG_SQM;
	const wFixed = DOOR_KG + HEATER_KG + STONES_KG;
	const wPlinths = input.numPlinths * PIER_KG;

	const buildingNetKg =
		wFraming + wPaneling + wTreated + wExterior + wInsulRoof + wFixed;
	const buildingGrossKg = Math.ceil(buildingNetKg * GROSS_MARGIN);
	const transportKg = Math.ceil(buildingGrossKg + wPlinths);

	let weightClass: WeightClass;
	let transportAdvice: string;
	let transportAdviceKey: WeightResult["transportAdviceKey"];

	if (transportKg <= LIGHT_THRESHOLD) {
		weightClass = "success";
		transportAdviceKey = "domain_weight_adviceLight";
		transportAdvice = t.domain_weight_adviceLight;
	} else if (transportKg <= MEDIUM_THRESHOLD) {
		weightClass = "warning";
		transportAdviceKey = "domain_weight_adviceMedium";
		transportAdvice = t.domain_weight_adviceMedium;
	} else {
		weightClass = "danger";
		transportAdviceKey = "domain_weight_adviceHeavy";
		transportAdvice = t.domain_weight_adviceHeavy;
	}

	return {
		buildingNetKg,
		buildingGrossKg,
		transportKg,
		plinthsKg: wPlinths,
		weightClass,
		transportAdvice,
		transportAdviceKey,
	};
}
